import React from 'react';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from 'lucide-react';
const ChevronLeft = ChevronLeftIcon as any;
const ChevronRight = ChevronRightIcon as any;

interface UnifiedPaginationProps {
    currentPage: number;
    onPrevious?: () => void;
    onNext?: () => void;
    canGoBack?: boolean;
    hasMore?: boolean;
    totalPages?: number;
    onPageChange?: (page: number) => void;
    perPage?: number;
    perPageOptions?: number[];
    onPerPageChange?: (value: number) => void;
}

const UnifiedPagination: React.FC<UnifiedPaginationProps> = ({
    currentPage,
    onPrevious,
    onNext,
    canGoBack = false,
    hasMore = false,
    totalPages,
    onPageChange,
    perPage,
    perPageOptions,
    onPerPageChange,
}) => {
    const isOffset = totalPages !== undefined && onPageChange !== undefined;

    const handlePrev = () => {
        if (isOffset) { if (currentPage > 1) onPageChange(currentPage - 1); }
        else { onPrevious?.(); }
    };

    const handleNext = () => {
        if (isOffset) { if (currentPage < totalPages!) onPageChange(currentPage + 1); }
        else { onNext?.(); }
    };

    const prevDisabled = isOffset ? currentPage <= 1 : !canGoBack;
    const nextDisabled = isOffset ? currentPage >= (totalPages ?? 1) : !hasMore;

    return (
        <div
            className="rounded-xl px-3 py-2.5"
            style={{
                background: 'var(--v2-bg-card)',
                border: '1px solid var(--v2-border-card)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
        >
            <div className="flex items-center justify-between gap-2">

                {/* Left: Per-page (hidden on smallest screens) */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    {perPage !== undefined && perPageOptions && onPerPageChange && (
                        <>
                            <span
                                className="hidden sm:inline text-[12px] font-medium whitespace-nowrap"
                                style={{ color: 'var(--v2-text-muted)' }}
                            >
                                Per page
                            </span>
                            <select
                                value={perPage}
                                onChange={(e) => onPerPageChange(Number(e.target.value))}
                                className="rounded-lg border px-2 py-1 text-[12px] font-semibold outline-none transition-all focus:ring-2 cursor-pointer"
                                style={{
                                    background: 'var(--v2-bg-card-inner)',
                                    borderColor: 'var(--v2-border-subtle)',
                                    color: 'var(--v2-text-primary)',
                                    minWidth: '52px',
                                }}
                            >
                                {perPageOptions.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </>
                    )}
                </div>

                {/* Center: Page pill indicator */}
                <div
                    className="flex items-center gap-1.5 rounded-full px-3 py-1 text-[16px] font-bold flex-shrink-0"
                    style={{
                        background: 'var(--v2-bg-card-inner)',
                        color: 'var(--v2-text-secondary)',
                        border: '1px solid var(--v2-border-subtle)',
                    }}
                >
                    <span
                        className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{ background: 'var(--v2-accent)' }}
                    />
                    <span>
                        Page {currentPage}
                        {isOffset && totalPages ? ` / ${totalPages}` : ''}
                    </span>
                </div>

                {/* Right: Prev / Next buttons */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Previous */}
                    <button
                        onClick={handlePrev}
                        disabled={prevDisabled}
                        className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                        style={{
                            color: prevDisabled ? 'var(--v2-text-muted)' : 'var(--v2-text-secondary)',
                            background: 'var(--v2-bg-card-inner)',
                            border: '1px solid var(--v2-border-subtle)',
                        }}
                    >
                        <ChevronLeft style={{ width: 14, height: 14 }} />
                        <span className="hidden sm:inline">Prev</span>
                    </button>

                    {/* Next */}
                    <button
                        onClick={handleNext}
                        disabled={nextDisabled}
                        className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                        style={{
                            color: nextDisabled ? 'var(--v2-text-muted)' : '#fff',
                            background: nextDisabled ? 'var(--v2-bg-card-inner)' : 'var(--v2-accent)',
                            border: nextDisabled ? '1px solid var(--v2-border-subtle)' : '1px solid transparent',
                        }}
                    >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight style={{ width: 14, height: 14 }} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnifiedPagination;
